import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { Part } from '../types';

interface PartsState {
  parts: Part[];
  selectedPartId: string | null;
  isLoading: boolean;
  error: string | null;
  actions: {
    fetchParts: () => Promise<void>;
    addPart: (part: Omit<Part, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<void>;
    updatePart: (id: string, updates: Partial<Part>) => Promise<void>;
    deletePart: (id: string) => Promise<void>;
    selectPart: (id: string | null) => void;
  };
}

const usePartsStore = create<PartsState>()(
  devtools(
    (set) => ({
      parts: [],
      selectedPartId: null,
      isLoading: false,
      error: null,
      actions: {
        fetchParts: async () => {
          set({ isLoading: true, error: null });
          try {
            const { data: userData } = await supabase.auth.getUser();
            if (!userData.user) {
              throw new Error('Not authenticated');
            }

            const { data, error } = await supabase
              .from('parts')
              .select('*')
              .eq('user_id', userData.user.id)
              .order('created_at', { ascending: true });

            if (error) throw error;
            set({ parts: data || [] });
          } catch (err) {
            console.error('Error fetching parts:', err);
            set({ error: err instanceof Error ? err.message : 'Failed to fetch parts' });
          } finally {
            set({ isLoading: false });
          }
        },

        addPart: async (part) => {
          set({ isLoading: true, error: null });
          try {
            const { data: userData } = await supabase.auth.getUser();
            if (!userData.user) {
              throw new Error('Not authenticated');
            }

            const { data, error } = await supabase
              .from('parts')
              .insert([{
                ...part,
                user_id: userData.user.id,
                emotions: part.emotions || [],
                notes: part.notes || []
              }])
              .select()
              .single();

            if (error) throw error;
            set((state) => ({ 
              parts: [...state.parts, data],
              error: null
            }));
          } catch (err) {
            console.error('Error adding part:', err);
            set({ error: err instanceof Error ? err.message : 'Failed to add part' });
            throw err;
          } finally {
            set({ isLoading: false });
          }
        },

        updatePart: async (id, updates) => {
          set({ isLoading: true, error: null });
          try {
            const { data: userData } = await supabase.auth.getUser();
            if (!userData.user) {
              throw new Error('Not authenticated');
            }

            const { data, error } = await supabase
              .from('parts')
              .update(updates)
              .eq('id', id)
              .eq('user_id', userData.user.id)
              .select()
              .single();

            if (error) throw error;
            set((state) => ({
              parts: state.parts.map((p) => p.id === id ? { ...p, ...data } : p),
              error: null
            }));
          } catch (err) {
            console.error('Error updating part:', err);
            set({ error: err instanceof Error ? err.message : 'Failed to update part' });
            throw err;
          } finally {
            set({ isLoading: false });
          }
        },

        deletePart: async (id) => {
          set({ isLoading: true, error: null });
          try {
            const { data: userData } = await supabase.auth.getUser();
            if (!userData.user) {
              throw new Error('Not authenticated');
            }

            const { error } = await supabase
              .from('parts')
              .delete()
              .eq('id', id)
              .eq('user_id', userData.user.id);

            if (error) throw error;
            set((state) => ({
              parts: state.parts.filter((p) => p.id !== id),
              selectedPartId: state.selectedPartId === id ? null : state.selectedPartId,
              error: null
            }));
          } catch (err) {
            console.error('Error deleting part:', err);
            set({ error: err instanceof Error ? err.message : 'Failed to delete part' });
            throw err;
          } finally {
            set({ isLoading: false });
          }
        },

        selectPart: (id) => {
          set({ selectedPartId: id });
        }
      }
    })
  )
);

export default usePartsStore;

export { usePartsStore }